import React, { useMemo } from "react";
import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { Controller, Control, useFieldArray } from "react-hook-form";
import { Switch } from "ui/gluestack/switch";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { AddIcon, TrashIcon } from "ui/gluestack/icon";
import {
  CHOICE_TYPES,
  INPUT_TYPES,
  QuestionTypeEnum,
} from "app/services/api/types/forms/enums/QuestionTypeEnum";
import { decimalValidation } from "ui/utils/Validators";
import { QuestionEditFormValues } from "../templates/QuestionEditModal";

interface Props {
  control: Control<QuestionEditFormValues>;
  type?: QuestionTypeEnum;
}

export function QuestionDetailsFormMolecule({ control, type }: Props) {
  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: "options",
  });

  const {
    fields: instructionFields,
    append: appendInstruction,
    remove: removeInstruction,
  } = useFieldArray({
    control,
    name: "instructions",
  });

  const addOption = () => {
    appendOption({ title: "", requires_description: false });
  };

  const addInstruction = () => {
    appendInstruction({ description: "" });
  };

  const isChoice = useMemo(
    () => (type ? CHOICE_TYPES.includes(type) : false),
    [type],
  );

  const isText = useMemo(
    () => (type ? INPUT_TYPES.includes(type) : false),
    [type],
  );
  const isNumber = type === QuestionTypeEnum.NUMBER;

  return (
    <View className="gap-4">
      {/* Content */}
      <View>
        <FormControlledInput
          id="question-content"
          name="content"
          control={control}
          label="Content"
          placeholder="Type the question..."
          isRequired
        />
      </View>

      {/* Unit (optional) */}
      {isText || isChoice ? (
        <View>
          <FormControlledInput
            id="question-unit"
            name="unit"
            control={control}
            label="Unit"
            placeholder="e.g. kg, cm, bpm"
          />
        </View>
      ) : null}

      {/* Required */}
      <View className="flex flex-row items-center justify-between">
        <Text className="text-sm text-gray-600">
          Does this question require an answer?
        </Text>
        <Controller
          control={control}
          name="required"
          render={({ field: { value, onChange } }) => (
            <Switch value={!!value} onValueChange={onChange} />
          )}
        />
      </View>

      {/* Number constraints */}
      {isNumber ? (
        <View className="flex flex-row gap-3">
          <View className="flex-1">
            <FormControlledInput
              id="question-min"
              name="min_value"
              control={control}
              label="Minimum"
              placeholder="min"
              type="numeric"
              rules={{ validate: decimalValidation }}
            />
          </View>
          <View className="flex-1">
            <FormControlledInput
              id="question-max"
              name="max_value"
              control={control}
              label="Maximum"
              placeholder="max"
              type="numeric"
              rules={{ validate: decimalValidation }}
            />
          </View>
        </View>
      ) : null}

      {/* Choice Options */}
      {isChoice ? (
        <View className="gap-2 mt-4">
          <View className="flex flex-row items-center justify-between">
            <Text className="text-sm text-gray-600">Options</Text>
            <Button size="sm" variant="link" onPress={addOption}>
              <ButtonText>Add option</ButtonText>
              <ButtonIcon as={AddIcon} />
            </Button>
          </View>

          {optionFields.length === 0 ? (
            <Text className="text-gray-500 text-sm">No options added yet.</Text>
          ) : null}

          {optionFields.map((opt, index) => (
            <View
              key={opt.id}
              className="border border-background-200 rounded-lg p-3 gap-2"
            >
              <View className="flex flex-row gap-4 items-center">
                <View className="flex-1">
                  <FormControlledInput
                    id={`option-title-${index}`}
                    name={`options.${index}.title`}
                    control={control}
                    placeholder={`Option ${index + 1}`}
                    rules={{ required: "Option title is required" }}
                  />
                </View>
                <Button
                  size="sm"
                  variant="link"
                  action="negative"
                  onPress={() => removeOption(index)}
                >
                  <ButtonIcon as={TrashIcon} />
                </Button>
              </View>
              <View className="flex flex-row items-center justify-between">
                <Text className="text-sm text-gray-600">
                  Requires description
                </Text>
                <Controller
                  control={control}
                  name={`options.${index}.requires_description`}
                  render={({ field: { value, onChange } }) => (
                    <Switch value={!!value} onValueChange={onChange} />
                  )}
                />
              </View>
            </View>
          ))}
        </View>
      ) : null}

      {/* Instructions */}

      <View className="gap-2 mt-4">
        <View className="flex flex-row items-center justify-between">
          <Text className="text-sm text-gray-600">Instructions</Text>
          <Button size="sm" variant="link" onPress={addInstruction}>
            <ButtonText>Add instruction</ButtonText>
            <ButtonIcon as={AddIcon} />
          </Button>
        </View>

        {instructionFields.length === 0 ? (
          <Text className="text-gray-500 text-sm">
            No instructions added yet.
          </Text>
        ) : null}

        {instructionFields.map((opt, index) => (
          <View
            key={opt.id}
            className="border border-background-200 rounded-lg p-3 gap-2"
          >
            <View className="flex flex-row gap-4 items-center">
              <View className="flex-1">
                <FormControlledInput
                  id={`instruction-title-${index}`}
                  name={`instructions.${index}.description`}
                  control={control}
                  placeholder={`Instruction ${index + 1}`}
                  rules={{ required: "Instruction description is required" }}
                />
              </View>
              <Button
                size="sm"
                variant="link"
                action="negative"
                onPress={() => removeInstruction(index)}
              >
                <ButtonIcon as={TrashIcon} />
              </Button>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}
